import { AnalyticsUserEvent } from '@fruitful/types';
import {
    Redirect,
    router,
    useLocalSearchParams,
    usePathname,
} from 'expo-router';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H2Emphasized,
    Image,
    isNonMobileWeb,
    SafeAreaView,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useViewEvents } from '../../events/hooks/useViewEvents';
import { captureException, track } from '../../logger/Logger';
import { getValidSessionBookingDoneRoute } from '../helpers';
import { useMembershipGuidanceSessionInfo } from '../hooks/useMembershipGuidanceSessionInfo';
import { GuidanceSessionType } from '../types/GuidanceSessions';

const INNER_MAX_WIDTH = 500;
const OUTER_MAX_WIDTH = INNER_MAX_WIDTH + 400;
const OUTER_MAX_HEIGHT = 1000;

const BookSessionDetails = () => {
    const { trackViewOnce } = useViewEvents();
    const { getColor } = useThemeColors();
    const pathname = usePathname();
    const { getSessionDetails } = useMembershipGuidanceSessionInfo();
    const { sessionType, doneRoute } = useLocalSearchParams<{
        sessionType?: GuidanceSessionType;
        doneRoute?: string;
    }>();

    const validDoneRoute = getValidSessionBookingDoneRoute(doneRoute as string);

    if (sessionType == null) {
        captureException(new Error(`Could not determine session type`));
        return <Redirect href={validDoneRoute} />;
    }

    trackViewOnce('GuidanceSessions/BookSessionDetails');

    const { title, description, image } = getSessionDetails(sessionType);

    return (
        <SafeAreaView
            style={{
                backgroundColor: getColor('$peach300'),
                flex: 1,
                ...(isNonMobileWeb && {
                    borderRadius: 32,
                    width: '96%',
                    height: '96%',
                    marginHorizontal: '2%',
                    marginVertical: '3%',
                    maxWidth: OUTER_MAX_WIDTH,
                    maxHeight: OUTER_MAX_HEIGHT,
                    alignSelf: 'center',
                }),
            }}
        >
            <YStack
                marginHorizontal={16}
                marginTop={16}
                flex={1}
                $sm={{
                    justifyContent: 'space-between',
                }}
            >
                <XStack justifyContent="space-between" alignItems="center">
                    <BackButton
                        fallbackRoute={validDoneRoute}
                        iconType={BackButtonIconType.ARROW_LEFT}
                        background="gray"
                        onBeforeGoingBack={() => {
                            track(AnalyticsUserEvent.BACK, {
                                pathname,
                                flow: 'GuidanceSessions',
                            });
                        }}
                    />
                    <BodySmallRegular textAlign="center" width={202}>
                        New session
                    </BodySmallRegular>
                    {/* Empty padding element to get the Heading centered with space-between */}
                    <View width={36} height={36} />
                </XStack>

                <YStack
                    paddingVertical={50}
                    alignItems="center"
                    alignSelf="center"
                    flexGrow={1}
                    flexShrink={1}
                    maxWidth={INNER_MAX_WIDTH}
                    gap={40}
                    $mobile_mini={{
                        gap: 24,
                    }}
                    $sm={{
                        justifyContent: 'center',
                        paddingTop: 0,
                    }}
                >
                    <YStack gap={4}>
                        <H2Emphasized textAlign="center">{title}</H2Emphasized>
                    </YStack>
                    <Image
                        source={image}
                        resizeMode="contain"
                        height={250}
                        width={250}
                        $mobile_mini={{
                            height: 200,
                            width: 200,
                        }}
                    />
                    <BodyMediumRegular
                        textAlign="center"
                        maxWidth={286}
                        $platform-web={{
                            maxWidth: '80%',
                        }}
                    >
                        {description}
                    </BodyMediumRegular>
                </YStack>

                <YStack
                    width="100%"
                    maxWidth={INNER_MAX_WIDTH}
                    alignSelf="center"
                    gap={8}
                    marginBottom={12}
                    $sm={{
                        marginBottom: 60,
                    }}
                >
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Next"
                        onPress={() => {
                            if (sessionType === GuidanceSessionType.DISCOVERY) {
                                router.navigate(
                                    '/onboarding/book-first-session',
                                );
                            } else {
                                router.navigate({
                                    pathname: '/guidance-sessions/book-session',
                                    params: {
                                        sessionType,
                                        doneRoute: validDoneRoute as string,
                                    },
                                });
                            }
                        }}
                    />
                </YStack>
            </YStack>
        </SafeAreaView>
    );
};

export default BookSessionDetails;
